/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module Angular-Core
 */

/**
 * @fileoverview Angular services need to be specified here to be downgraded and used in
 *     AngularJS.
 */
import angular from 'angular';

import { NgZone } from '@angular/core';

import { downgradeInjectable } from '@angular/upgrade/static';
import {
    I18nContext,
    I18nService,
    L10nService,
    LocaleService,
    VIPService,
} from '@vmw/ngx-vip';

import { Store } from '@ngrx/store';
import { STORE_TOKEN } from 'ng/root-store/root-store.tokens';
import { InitialDataService } from 'ng/modules/core/services/initial-data/initial-data.service';
import { MyAccountService } from 'ng/modules/core/services/my-account.service';
import { ControllerFaultsService } from 'ng/modules/core/services/controller-faults.service';
import { CSRFPolicyCollection } from 'collections/csrf-policy.collection.factory';
import { AlertSyslogConfigCollection } from 'collections/alert-syslog-config.collection.factory';
import {
    FileObjectCollection,
    FILE_OBJECT_COLLECTION_TOKEN,
} from 'collections/file-object.collection.factory';
import { UpdateService } from 'ng/modules/update/services/update.service';
import { GSLB, GSLB_TOKEN } from 'ng/modules/items/factories/gslb.item.factory';

import {
    NavigationService,
    NAVIGATION_SERVICE_TOKEN,
} from 'ng/modules/navigation/services/navigation.service';

import {
    AviAlertService,
    ConvertService,
    CONVERT_SERVICE_TOKEN,
    DevLoggerService,
    DialogService,
    DIALOG_SERVICE_TOKEN,
    FullModalService,
    FULL_MODAL_SERVICE_TOKEN,
    NotificationService,
    OverlayRemovalService,
    OVERLAY_REMOVAL_SERVICE_TOKEN,
    StateAccessService,
    STATE_ACCESS_SERVICE_TOKEN,
    TenantService,
} from './angular/modules/core/services';

import { AppLevelAlertsService } from './angular/modules/core/services/app-level-alerts.service';

import {
    CentralLicenseAlertsService,
} from './angular/modules/core/services/central-license-alerts';

import { WelcomeService } from './angular/modules/welcome';
import { VerifyCloudConnectorUserHostModalService }
    from './angular/modules/cloud-services/services/verify-cloud-connector-user-host-modal.service';
import { RecommendationDialogService }
    from './angular/modules/logs/services/recommendation-dialog.service';

import {
    CERTIFICATE_EXPORT_MODAL_SERVICE_TOKEN,
    HsmGroupDownloadCertificateModalService,
    SslCertificateExportModalService,
} from './angular/modules/security';
import { VirtualServiceStateService } from './angular/modules/virtual-service/services';
import { PoolStateService } from './angular/modules/pool/services/pool-state.service';

import { VsLogsDataTransformerService }
    from './angular/modules/vs-logs/services/vs-log-data-transformer.service';

import {
    ALERT_SYSLOG_CONFIG_COLLECTION_TOKEN,
    APP_LEVEL_ALERTS_SERVICE_TOKEN,
    CENTRAL_LICENSE_ALERT_SERVICE_TOKEN,
    CONTROLLER_FAULTS_SERVICE_TOKEN,
    CSRF_POLICY_COLLECTION_TOKEN,
    HSM_GROUP_DOWNLOAD_CERTIFICATE_MODAL_SERVICE_TOKEN,
    POOL_STATE_SERVICE_TOKEN,
    RECOMMENDATION_DIALOG_SERVICE_TOKEN,
    UPDATE_SERVICE_TOKEN,
    VERIFY_CLOUD_CONNECTOR_USER_HOST_MODAL_SERVICE_TOKEN,
    VIRTUAL_SERVICE_STATE_SERVICE_TOKEN,
    VS_LOG_DATA_TRANSFORMER_SERVICE_TOKEN,
} from './downgrade-services.tokens';

const downgradeServices = [
    {
        ajsName: 'TenantService',
        service: TenantService,
    },
    {
        ajsName: 'notificationService',
        service: NotificationService,
    },
    {
        ajsName: FULL_MODAL_SERVICE_TOKEN,
        service: FullModalService,
    },
    {
        ajsName: 'l10nService',
        service: L10nService,
    },
    {
        ajsName: 'i18nService',
        service: I18nService,
    },
    {
        ajsName: 'welcomeService',
        service: WelcomeService,
    },
    {
        ajsName: 'localeService',
        service: LocaleService,
    },
    {
        ajsName: 'vipService',
        service: VIPService,
    },
    {
        ajsName: 'i18nContext',
        service: I18nContext,
    },
    {
        ajsName: 'devLoggerService',
        service: DevLoggerService,
    },
    {
        ajsName: DIALOG_SERVICE_TOKEN,
        service: DialogService,
    },
    {
        ajsName: CERTIFICATE_EXPORT_MODAL_SERVICE_TOKEN,
        service: SslCertificateExportModalService,
    },
    {
        ajsName: 'aviAlertService',
        service: AviAlertService,
    },
    {
        ajsName: HSM_GROUP_DOWNLOAD_CERTIFICATE_MODAL_SERVICE_TOKEN,
        service: HsmGroupDownloadCertificateModalService,
    },
    {
        ajsName: APP_LEVEL_ALERTS_SERVICE_TOKEN,
        service: AppLevelAlertsService,
    },
    {
        ajsName: CENTRAL_LICENSE_ALERT_SERVICE_TOKEN,
        service: CentralLicenseAlertsService,
    },
    {
        ajsName: VERIFY_CLOUD_CONNECTOR_USER_HOST_MODAL_SERVICE_TOKEN,
        service: VerifyCloudConnectorUserHostModalService,
    },
    {
        ajsName: RECOMMENDATION_DIALOG_SERVICE_TOKEN,
        service: RecommendationDialogService,
    },
    {
        ajsName: STORE_TOKEN,
        service: Store,
    },
    {
        ajsName: 'NgZone',
        service: NgZone,
    },
    {
        ajsName: VIRTUAL_SERVICE_STATE_SERVICE_TOKEN,
        service: VirtualServiceStateService,
    },
    // Todo AV-177044: Remove once modal is migrated to angular.
    {
        ajsName: 'initialDataService',
        service: InitialDataService,
    },
    {
        ajsName: 'myAccount',
        service: MyAccountService,
    },
    {
        ajsName: CONTROLLER_FAULTS_SERVICE_TOKEN,
        service: ControllerFaultsService,
    },
    {
        ajsName: CSRF_POLICY_COLLECTION_TOKEN,
        service: CSRFPolicyCollection,
    },
    {
        ajsName: OVERLAY_REMOVAL_SERVICE_TOKEN,
        service: OverlayRemovalService,
    },
    {
        ajsName: UPDATE_SERVICE_TOKEN,
        service: UpdateService,
    },
    {
        ajsName: ALERT_SYSLOG_CONFIG_COLLECTION_TOKEN,
        service: AlertSyslogConfigCollection,
    },
    {
        ajsName: STATE_ACCESS_SERVICE_TOKEN,
        service: StateAccessService,
    },
    {
        ajsName: NAVIGATION_SERVICE_TOKEN,
        service: NavigationService,
    },
    {
        ajsName: GSLB_TOKEN,
        service: GSLB,
    },
    {
        ajsName: CONVERT_SERVICE_TOKEN,
        service: ConvertService,
    },
    {
        ajsName: FILE_OBJECT_COLLECTION_TOKEN,
        service: FileObjectCollection,
    },
    {
        ajsName: VS_LOG_DATA_TRANSFORMER_SERVICE_TOKEN,
        service: VsLogsDataTransformerService,
    },
    {
        ajsName: POOL_STATE_SERVICE_TOKEN,
        service: PoolStateService,
    },
];

downgradeServices.forEach((serviceData: any): void => {
    const { ajsName, service } = serviceData;

    if (!ajsName) {
        throw new Error(`Angular JS name needed for ${service}`);
    }

    angular.module('aviApp').service(ajsName, downgradeInjectable(service) as any);
});
